import type { JSX } from "solid-js";
import { For, Show, createEffect, createMemo, createSignal, onMount } from "solid-js";
import { createEdgeScroll } from "~/lib/use-edge-scroll";
import { persistentRef } from "~/lib/sticky-state";
import { FEATURES } from "~/lib/features";
import { DriverCard, exportDriverCardBlob, ogCardFingerprint } from "~/components/driver-card";
import { DuelCard } from "~/components/duel-card";
import { DuelHistory } from "~/components/duel-history";
import { Emboss } from "~/components/emboss";
import { GamertagEditor } from "~/components/gamertag-editor";
import { PageContainer } from "~/components/layout/page";
import { TierBadge } from "~/components/tier-badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { ogApi, playersApi } from "~/lib/api";
import { useAuth } from "~/lib/auth";
import {
  CAR_CLASS_COLOR_VAR,
  CAR_CLASS_LABELS,
  RACE_TYPE_COLOR_VAR,
  RACE_TYPE_LABELS,
  formatDate,
} from "~/lib/utils";

import {
  TIER_ORDER,
  type CarClass,
  type RaceType,
  type RankingHistoryEntry,
  type Tier,
} from "system";
import { createQuery } from "@tanstack/solid-query";
import { createFileRoute, Link } from "@tanstack/solid-router";
import { QueryKey } from "~/lib/query-keys";
import { requireFeature } from "~/lib/features";
import { Dynamic } from "solid-js/web";

// Black tier uses its text color for gradients; its bg color (#09090b) is invisible on dark surfaces
function tierLineColor(tier: string): string {
  return `var(--color-tier-${tier === "black" ? "black-text" : tier})`;
}

function tierDirection(entry: RankingHistoryEntry): "up" | "down" | "first" {
  if (!entry.oldTier) return "first";
  const o = TIER_ORDER[entry.oldTier as Tier] ?? 0;
  const n = TIER_ORDER[entry.newTier as Tier] ?? 0;
  return n > o ? "up" : n < o ? "down" : "first";
}

function sourceLabel(entry: RankingHistoryEntry): string {
  switch (entry.source) {
    case "tournament": return entry.tournamentName ?? "Tournament";
    case "duel": return "Duel result";
    case "expiry": return "No-show demotion";
    case "admin": return "Admin adjustment";
    case "revert": return "Reverted";
    default: return "Ranking sync"; // sync
  }
}

function groupHistory(entries: RankingHistoryEntry[]) {
  const map = new Map<string, { raceType: RaceType; carClass: CarClass; entries: RankingHistoryEntry[] }>();
  for (const e of entries) {
    const key = `${e.raceType}:${e.carClass}`;
    if (!map.has(key)) map.set(key, { raceType: e.raceType as RaceType, carClass: e.carClass as CarClass, entries: [] });
    map.get(key)!.entries.push(e);
  }
  // rankingHistory arrives newest-first; each column renders the same way, so the
  // most recent rung sits at the top (top-anchored) and history trails downward.
  return [...map.values()].sort((a, b) => {
    // Most recently active category first → leftmost in the carousel.
    const aLast = a.entries[0]?.changedAt ?? "";
    const bLast = b.entries[0]?.changedAt ?? "";
    return bLast.localeCompare(aLast);
  });
}

// One horizontal carousel of vertical category columns. X = which category,
// Y = time within it (newest at top). A single edge-scroll drives the whole
// strip; columns are its direct children so snap targets land on column starts.
function ProgressionCarousel(props: { history: RankingHistoryEntry[]; scrollKey: string }) {
  const scroll = createEdgeScroll();
  // Sticky horizontal offset, keyed per profile, so switching tabs (the History
  // tab unmounts this) or an HMR re-run lands back where it was scrolled to.
  const scrollBox = persistentRef(`progression:${props.scrollKey}`, 0);
  let ref: HTMLDivElement | undefined;
  onMount(() => {
    requestAnimationFrame(() => { if (ref) ref.scrollLeft = scrollBox.current; });
  });
  // Memoize the grouping so an unrelated parent re-render doesn't hand <For> a
  // fresh array of new group objects and remount every column.
  const historyGroups = createMemo(() => groupHistory(props.history));
  return (
    <div
      ref={(el) => { ref = el; scroll.ref(el); }}
      onScroll={() => { if (ref) scrollBox.current = ref.scrollLeft; }}
      class="flex items-start gap-4 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden snap-x snap-proximity mx-[calc(50%-50vw)] px-[calc(50vw-50%)]"
    >
      <For each={historyGroups()}>
        {(group) => (
          <CategoryColumn
            raceType={group.raceType}
            carClass={group.carClass}
            entries={group.entries}
          />
        )}
      </For>
    </div>
  );
}

function CategoryColumn(props: { raceType: RaceType; carClass: CarClass; entries: RankingHistoryEntry[] }) {
  return (
    <div class="shrink-0 snap-start w-32 flex flex-col">
      {/* Fixed-height header bottom-aligns its text so the newest rung lands on
          the same top line across columns regardless of label wrapping. */}
      <div class="flex justify-center px-4 py-2 pointer-events-none">
        <span class="text-label-lg font-semibold uppercase tracking-wide text-outline-sm whitespace-nowrap">
          <span
            style={{ color: RACE_TYPE_COLOR_VAR[props.raceType] }}
          >
            {RACE_TYPE_LABELS[props.raceType]}
          </span>
          {" · "}
          <span
            style={{ color: CAR_CLASS_COLOR_VAR[props.carClass] }}
          >
            {CAR_CLASS_LABELS[props.carClass]}
          </span>
        </span>
      </div>
      <For each={props.entries}>
        {(entry, i) => {
          const dir = tierDirection(entry);
          const src = () => entry.oldTier ? sourceLabel(entry) : "First placement";
          const isOldest = () => i() === props.entries.length - 1;
          const olderTier = () => props.entries[i() + 1]?.newTier;
          const connector = (): JSX.CSSProperties => ({
            background: `linear-gradient(to bottom, ${tierLineColor(entry.newTier ?? "white")}, ${tierLineColor(olderTier()! ?? "white")})`,
          });
          return (
            <>
              {/* Checkpoint */}
              <div class="relative isolate shrink-0 flex flex-col items-center">
                {/* Duel-won (or lost) rungs carry the matchup seal behind the record; non-duel sources (tournament, sync) leave it bare. isolate keeps the -z-10 stamp behind this checkpoint's own content. */}
                <Show when={entry.seal}>
                  {(seal) => (
                    <Emboss
                      mark={seal()}
                      class="absolute left-1/2 top-0 -z-10 h-12 w-24 -translate-x-1/2"
                    />
                  )}
                </Show>
                <div class="h-7 flex items-center">
                  <TierBadge
                    tier={entry.newTier as Tier}
                    size="sm"
                    class={dir === "down" ? "ring-1 ring-red-500/50" : undefined}
                  />
                </div>
                <span
                  class="text-xs text-text-muted font-medium text-center line-clamp-1 w-full px-2 mt-1"
                  title={src()}
                >
                  {src()}
                </span>
                <span class="text-label-sm text-text-faint">{formatDate(entry.changedAt)}</span>
              </div>

              {/* Gradient connector down to the older checkpoint */}
              <Show when={!isOldest()}>
                <div class="shrink-0 h-8 flex justify-center">
                  <div class="w-0.5 h-full rounded-full" style={connector()} />
                </div>
              </Show>
            </>
          );
        }}
      </For>
    </div>
  );
}

const PROFILE_TABS = ["progression", "duels", "history"] as const;
type ProfileTab = typeof PROFILE_TABS[number];

function validateEnum<T extends string>(value: unknown, options: readonly T[]): T | undefined {
  return (options as unknown as string[]).includes(value as string) ? (value as T) : undefined;
}

export const Route = createFileRoute("/profile/$username")({
  beforeLoad: () => requireFeature("profile"),
  validateSearch: (search: Record<string, unknown>) => ({
    tab: validateEnum(search.tab, PROFILE_TABS),
  }),
  component: ProfilePage,
});

function ProfilePage() {
  const params = Route.useParams();
  const { state } = useAuth();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  // Logged-out visitors get the public view: identity, card, and tier
  // progression. The active-duel and history tabs are members-only, so their
  // tab values are never selectable and progression is the only landing tab.
  const isMember = () => !!state.user;
  const tab = () => (isMember() ? search().tab ?? "progression" : "progression");
  const setTab = (t: string) => navigate({ search: (prev) => ({ ...prev, tab: t as ProfileTab }), replace: true });

  const profile = createQuery(() => ({
    queryKey: [QueryKey.Profile, "by-username", params().username.toLowerCase()],
    queryFn: () => playersApi.getByUsername(params().username),
    enabled: !!params().username,
    // TODO: invalidate the profile cache from the submit-match mutation so recentDuels
    // updates immediately instead of waiting for this staleTime to expire.
    staleTime: 30_000,
  }));

  const isOwnProfile = () => state.user?.username?.toLowerCase() === params().username.toLowerCase();

  const myProfile = createQuery(() => ({
    queryKey: [QueryKey.Profile, state.user?.id],
    queryFn: () => playersApi.get(state.user!.id),
    enabled: !!state.user?.id && !isOwnProfile(),
    staleTime: 5 * 60_000,
  }));

  // OG card upload - when the fingerprint of the live card differs from the one
  // captured in the last uploaded image (or nothing was uploaded yet), render the
  // hidden public card to a PNG and POST it with its fingerprint. Idempotent: once
  // uploaded for a fingerprint we stop retrying it.
  const [cardEl, setCardEl] = createSignal<HTMLElement | null>(null);
  const [uploadedKey, setUploadedKey] = createSignal<string | null>(null);
  createEffect(() => {
    const p = profile.data;
    const el = cardEl();
    if (!p || !el) return;
    // The server restricts uploads to the profile owner and admins. Admins get
    // the escape hatch of refreshing any stale card by visiting it, without
    // waiting for that driver to log in. Skipping everyone else avoids dead 403s
    // on every visit to another driver's page.
    if (!isOwnProfile() && state.user?.role !== "admin") return;
    const live = ogCardFingerprint(p);
    if (p.ogImageKey === live) return;
    if (uploadedKey() === live) return;
    // Mark first, then upload - keeps us from queuing duplicate work if the
    // effect re-runs while the upload is in flight.
    setUploadedKey(live);
    void (async () => {
      // Let SVG art and fonts settle before snapshotting.
      await new Promise((r) => setTimeout(r, 1500));
      const blob = await exportDriverCardBlob(el).catch(() => null);
      if (!blob) {
        setUploadedKey(null);
        return;
      }
      await ogApi.upload(p.username, blob, live).catch(() => setUploadedKey(null));
    })();
  });

  return (
    <PageContainer>
      <Show
        when={profile.data}
        fallback={<div class="h-[200px] bg-surface-2 rounded-xl animate-pulse" />}
      >
        {(p) => (
          <div class="space-y-6">
              <Dynamic component={isOwnProfile() ? Link : 'div'} to="/dashboard" class="block">
                <DriverCard
                  profile={p()}
                  viewer={myProfile.data ?? undefined}
                />
              </Dynamic>

              {/* Canonical public card, snapshotted for the shared OG image. It has
                  no viewer, so viewer-relative affordances (the challenge button,
                  owner controls) can never bake into a public preview. Mounted only
                  for the owner and admins, the two who are allowed to upload. */}
              <Show when={isOwnProfile() || state.user?.role === "admin"}>
                <div aria-hidden="true" class="fixed -left-[9999px] top-0 pointer-events-none">
                  <DriverCard profile={p()} onCardElement={setCardEl} />
                </div>
              </Show>

              <Show when={isMember()}>
                <GamertagEditor current={p().gamertag} editable={isOwnProfile()} />
              </Show>

              {/* ── Tabs ──────────────────────────────────────────── */}
              <Tabs value={tab()} onChange={setTab}>
                <TabsList>
                  <TabsTrigger value="progression">Progression</TabsTrigger>
                  <Show when={isMember()}>
                    <TabsTrigger value="duels">Active Duels</TabsTrigger>
                  </Show>
                  <Show when={isMember() && FEATURES.matchHistory}>
                    <TabsTrigger value="history">History</TabsTrigger>
                  </Show>
                </TabsList>

                <TabsContent value="progression">
                  <Show
                    when={p().rankingHistory.length > 0}
                    fallback={<Empty text="No tier changes recorded yet." />}
                  >
                    <ProgressionCarousel history={p().rankingHistory} scrollKey={params().username.toLowerCase()} />
                  </Show>
                </TabsContent>

                <Show when={isMember()}>
                  <TabsContent value="duels">
                    <Show
                      when={p().activeDuels.length > 0}
                      fallback={<Empty text="No active duels." />}
                    >
                      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <For each={p().activeDuels}>
                          {(duel) => <DuelCard duel={duel} />}
                        </For>
                      </div>
                    </Show>
                  </TabsContent>

                  <TabsContent value="history">
                    <Show
                      when={p().recentDuels.length > 0}
                      fallback={<Empty text="No completed duels." />}
                    >
                      <DuelHistory duels={p().recentDuels} viewerId={p().id} summary vacationUntil={p().vacationUntil} />
                    </Show>
                  </TabsContent>
                </Show>
              </Tabs>
            </div>
          )}
      </Show>
    </PageContainer>
  );
}

function Empty(props: { text: string }) {
  return <p class="text-sm text-text-faint text-center py-8">{props.text}</p>;
}
